import { ApplyElementChangesBody, CreateElementBody } from '../api/reports.api';
import { Report } from '../types';
import { AiElementChange, AiGeneratedElement, AiInlineMeasure } from '../types/ai-report';

interface InlineMeasureBody {
  key: string;
  label: string;
  measureType: string;
  formulaString: string;
  decimals?: number;
  unit?: string;
}

const toInlineMeasureBody = (measure: AiInlineMeasure): InlineMeasureBody => {
  const body: InlineMeasureBody = {
    key: measure.key,
    label: measure.label,
    measureType: measure.measureType,
    formulaString: measure.formulaString,
  };
  if (measure.decimals !== null) body.decimals = measure.decimals;
  if (measure.unit !== null) body.unit = measure.unit;
  return body;
};

/** The fields the AI controls; everything else on an updated element (filters, styling) is kept. */
const toElementBody = (
  element: AiGeneratedElement,
  config: Record<string, unknown>
): CreateElementBody => ({
  type: element.elementType,
  position: element.position,
  config,
  reportType: element.reportType,
  columns: element.columns,
  groupBy: element.groupBy,
  pivotDimension: element.pivotDimension,
  sortBy: element.sortBy,
  sortOrder: element.sortOrder,
  customMeasures: element.customMeasures.map(toInlineMeasureBody),
});

/** Turns an AI proposal into one batch request, so the canvas changes all-or-nothing. */
export const mapAiChangesToApplyBody = (
  changes: AiElementChange[],
  report: Report
): ApplyElementChangesBody => {
  const body: ApplyElementChangesBody = { deleteElementIds: [], updates: [], creates: [] };
  for (const { action, elementId, element } of changes) {
    if (action === 'DELETE' && elementId) {
      body.deleteElementIds.push(elementId);
    } else if (action === 'UPDATE' && elementId && element) {
      const current = report.elements.find(el => el.id === elementId);
      const config = { ...current?.config };
      if (element.title) config.title = element.title;
      body.updates.push({ elementId, changes: toElementBody(element, config) });
    } else if (action === 'ADD' && element) {
      body.creates.push(toElementBody(element, element.title ? { title: element.title } : {}));
    }
  }
  return body;
};
